<template>
  <div v-if="supported" class="erp-mappings-panel card-block">
    <div class="panel-header">
      <h5 class="panel-title">{{ $t("tools.erp_integration.mappings_title") }}</h5>
      <div class="panel-actions">
        <button
          class="btn btn-sm btn-outline-primary"
          :disabled="loading || autoMatchBusy || (showSeasonSelector && !selectedSeasonUuid)"
          :title="!autoMatchEnabled ? $t('tools.erp_integration.auto_match_no_rules') : null"
          @click="$emit('auto-match')"
        >
          {{ $t("tools.erp_integration.run_auto_match") }}
        </button>
        <DxButton
          class="mapping-refresh-button"
          icon="refresh"
          :hint="$t('tools.erp_integration.refresh_mappings')"
          :element-attr="{ 'aria-label': $t('tools.erp_integration.refresh_mappings') }"
          :disabled="loading || autoMatchBusy"
          styling-mode="outlined"
          type="normal"
          :on-click="onRefreshMappings"
        />
      </div>
    </div>

    <div class="panel-filters">
      <div v-if="showSeasonSelector" class="season-filter">
        <label class="season-filter__label">{{ $t("tools.erp_integration.parcel_mapping_season_filter") }}</label>
        <DxSelectBox
          class="season-filter__select"
          :data-source="seasonOptions"
          display-expr="label"
          value-expr="value"
          :search-enabled="true"
          :show-clear-button="true"
          :placeholder="$t('tools.erp_integration.parcel_mapping_select_season')"
          :value="selectedSeasonUuid"
          @value-changed="onSeasonChanged"
        />
      </div>

      <div class="season-filter">
        <label class="season-filter__label">{{ $t("tools.erp_integration.mapping_status_filter") }}</label>
        <DxSelectBox
          class="season-filter__select"
          :data-source="mappingStatusOptions"
          display-expr="label"
          value-expr="value"
          :value="mappingStatus"
          @value-changed="onMappingStatusValueChanged"
        />
      </div>
    </div>

    <div v-if="loading" class="text-muted">
      {{ $t("tools.erp_integration.loading_mappings") }}
    </div>

    <div v-if="showSeasonSelector && !selectedSeasonUuid" class="season-filter-empty">
      {{ $t("tools.erp_integration.parcel_mapping_select_season_hint") }}
    </div>

    <DxDataGrid
      v-else
      ref="mappingsGrid"
      class="smagria-grid erp-mappings-grid"
      :key-expr="keyField"
      :data-source="dataSource"
      :remote-operations="remoteOperations"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="false"
      :allowColumnResizing="true"
      :row-alternation-enabled="true"
      :no-data-text="$t('common.no_data_text')"
      style="min-height: 320px"
    >
      <DxPaging :page-size="25" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="allowedPageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxHeaderFilter :visible="true" />
      <DxFilterRow :visible="true" apply-filter="auto" />
      <DxColumn
        data-field="display_name"
        :caption="$t('common.name')"
        cell-template="nameCell"
        :min-width="160"
        :allow-sorting="false"
        :allow-filtering="true"
        :allow-header-filtering="true"
        :filter-operations="['contains']"
        selected-filter-operation="contains"
      />
      <DxColumn
        v-if="entityCode === 'WAREHOUSES'"
        data-field="department_name"
        :caption="$t('tools.erp_integration.department')"
        cell-template="departmentCell"
        :min-width="160"
        :allow-sorting="false"
        :allow-filtering="true"
        :allow-header-filtering="true"
        :filter-operations="['contains']"
        selected-filter-operation="contains"
      />
      <DxColumn
        v-if="entityCode === 'PARCELS'"
        data-field="code"
        :caption="$t('common.code')"
        :min-width="120"
        :allow-sorting="false"
        :allow-filtering="true"
        :allow-header-filtering="true"
        :filter-operations="['contains']"
        selected-filter-operation="contains"
      />
      <DxColumn
        v-if="entityCode === 'PARTNERS'"
        data-field="nr_reg"
        :caption="$t('common.nr_reg')"
        :min-width="140"
        :allow-sorting="false"
        :allow-filtering="true"
        :allow-header-filtering="true"
        :filter-operations="['contains']"
        selected-filter-operation="contains"
      />
      <DxColumn
        v-if="entityCode === 'PARTNERS'"
        data-field="id_number"
        :caption="$t('stock_management.partners.id_number')"
        :min-width="140"
        :allow-sorting="false"
        :allow-filtering="true"
        :allow-header-filtering="true"
        :filter-operations="['contains']"
        selected-filter-operation="contains"
      />
      <DxColumn
        :caption="entityCode === 'LEGAL_ENTITIES' ? $t('tools.erp_integration.erp_subunit_label') : erpLabel"
        cell-template="erpCell"
        :min-width="220"
        :allow-sorting="false"
        :allow-filtering="false"
      />
      <DxColumn
        v-if="entityCode === 'LEGAL_ENTITIES'"
        :caption="$t('tools.erp_integration.erp_partner_label')"
        cell-template="partnerCell"
        :min-width="220"
        :allow-sorting="false"
        :allow-filtering="false"
      />
      <DxColumn
        v-if="entityCode === 'LEGAL_ENTITIES'"
        :caption="$t('tools.erp_integration.erp_consum_warehouse_label')"
        cell-template="consumWarehouseCell"
        :min-width="220"
        :allow-sorting="false"
        :allow-filtering="false"
      />
      <DxColumn
        v-if="entityCode === 'PRODUCTS'"
        :caption="$t('tools.erp_integration.erp_service_article_label')"
        cell-template="serviceArticleCell"
        :min-width="220"
        :allow-sorting="false"
        :allow-filtering="false"
      />
      <DxColumn
        :caption="$t('common.actions')"
        cell-template="actionsCell"
        :width="entityCode === 'PRODUCTS' ? 100 : 70"
        :allow-sorting="false"
        :allow-filtering="false"
      />

      <template #nameCell="{ data }">
        <span>{{ displaySourceName(data.data) }}</span>
      </template>

      <template #partnerCell="{ data }">
        <DxSelectBox
          class="erp-mapping-select"
          :data-source="partnerSelectDataSource"
          display-expr="label"
          value-expr="value"
          :search-enabled="true"
          search-expr="label"
          search-mode="contains"
          :min-search-length="0"
          :search-timeout="300"
          :show-clear-button="true"
          :disabled="isMappingBusy(data.data)"
          :placeholder="$t('tools.erp_integration.erp_entry_optional')"
          :value="partnerValue(data.data)"
          @value-changed="(e) => onPartnerSelectionChanged(data.data, e.value)"
        />
      </template>

      <template #consumWarehouseCell="{ data }">
        <DxSelectBox
          class="erp-mapping-select"
          :data-source="consumWarehouseSelectDataSource"
          display-expr="label"
          value-expr="value"
          :search-enabled="true"
          search-expr="label"
          search-mode="contains"
          :min-search-length="0"
          :search-timeout="300"
          :show-clear-button="true"
          :disabled="isMappingBusy(data.data)"
          :placeholder="$t('tools.erp_integration.erp_entry_optional')"
          :value="consumWarehouseValue(data.data)"
          @value-changed="(e) => onConsumWarehouseSelectionChanged(data.data, e.value)"
        />
      </template>

      <template #departmentCell="{ data }">
        <span>{{ getDepartmentName(data.data) }}</span>
      </template>

      <template #erpCell="{ data }">
        <DxSelectBox
          v-if="!isStaticMode"
          class="erp-mapping-select"
          :data-source="erpSelectDataSource"
          display-expr="label"
          value-expr="value"
          :search-enabled="true"
          search-expr="label"
          search-mode="contains"
          :min-search-length="0"
          :search-timeout="300"
          :show-clear-button="true"
          :disabled="isMappingBusy(data.data)"
          :placeholder="$t('tools.erp_integration.erp_entry_optional')"
          :value="selectedValue(data.data)"
          @value-changed="(e) => onSelectionChanged(data.data, e.value)"
        />
        <input
          v-else
          class="form-control form-control-sm erp-mapping-input"
          type="text"
          :disabled="isMappingBusy(data.data)"
          :value="selectedValue(data.data)"
          :placeholder="$t('tools.erp_integration.static_value_placeholder')"
          @change="onSelectionChanged(data.data, $event.target.value)"
        />
      </template>

      <template #actionsCell="{ data }">
        <div class="mapping-actions">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger mapping-delete-button"
            :title="$t(entityCode === 'PRODUCTS' ? 'tools.erp_integration.clear_product_mapping' : 'common.delete')"
            :aria-label="$t(entityCode === 'PRODUCTS' ? 'tools.erp_integration.clear_product_mapping' : 'common.delete')"
            :disabled="isMappingBusy(data.data) || (entityCode === 'PRODUCTS' ? !selectedValue(data.data) : !mappingBySourceKey[sourceKey(data.data)])"
            @click="clearMapping(data.data)"
          >
            <i class="dx-icon dx-icon-trash" aria-hidden="true"></i>
          </button>
          <button
            v-if="entityCode === 'PRODUCTS'"
            type="button"
            class="btn btn-sm btn-outline-danger mapping-delete-button"
            :title="$t('tools.erp_integration.clear_service_mapping')"
            :aria-label="$t('tools.erp_integration.clear_service_mapping')"
            :disabled="isMappingBusy(data.data) || !serviceArticleValue(data.data)"
            @click="onServiceArticleSelectionChanged(data.data, null)"
          >
            <i class="dx-icon dx-icon-trash" aria-hidden="true"></i>
          </button>
        </div>
      </template>

      <template #serviceArticleCell="{ data }">
        <DxSelectBox
          v-if="!isStaticMode"
          class="erp-mapping-select"
          :data-source="serviceArticleSelectDataSource"
          display-expr="label"
          value-expr="value"
          :search-enabled="true"
          search-expr="label"
          search-mode="contains"
          :min-search-length="0"
          :search-timeout="300"
          :show-clear-button="true"
          :disabled="isMappingBusy(data.data)"
          :placeholder="$t('tools.erp_integration.erp_entry_optional')"
          :value="serviceArticleValue(data.data)"
          @value-changed="(e) => onServiceArticleSelectionChanged(data.data, e.value)"
        />
        <input
          v-else
          class="form-control form-control-sm erp-mapping-input"
          type="text"
          :disabled="isMappingBusy(data.data)"
          :value="serviceArticleValue(data.data)"
          :placeholder="$t('tools.erp_integration.static_value_placeholder')"
          @change="onServiceArticleSelectionChanged(data.data, $event.target.value)"
        />
      </template>
    </DxDataGrid>
  </div>
</template>

<script>
import DxDataGrid, {
  DxColumn,
  DxHeaderFilter,
  DxPaging,
  DxPager,
  DxFilterRow,
} from "devextreme-vue/data-grid";
import DxSelectBox from "devextreme-vue/select-box";
import DxButton from "devextreme-vue/button";
import CustomStore from "devextreme/data/custom_store";
import axios from "@/plugins/axios";
import * as endpoints from "@/store/api/endpoints";

function extractContainsSearch(filter) {
  if (!filter) return "";
  if (typeof filter === "string") return filter;
  if (Array.isArray(filter) && filter.length === 3 && filter[1] === "contains" && typeof filter[2] === "string") {
    return filter[2];
  }
  if (Array.isArray(filter)) {
    for (const item of filter) {
      if (Array.isArray(item)) {
        const found = extractContainsSearch(item);
        if (found) return found;
      }
    }
  }
  return "";
}

export default {
  name: "ErpEntityMappingsPanel",
  components: {
    DxDataGrid,
    DxColumn,
    DxHeaderFilter,
    DxPaging,
    DxPager,
    DxFilterRow,
    DxSelectBox,
    DxButton,
  },
  props: {
    supported: {
      type: Boolean,
      default: false,
    },
    dataSource: {
      default: () => [],
    },
    keyField: {
      type: String,
      default: "uuid",
    },
    mappingBySourceKey: {
      type: Object,
      default: () => ({}),
    },
    erpLabel: {
      type: String,
      default: "",
    },
    isStaticMode: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    busySourceKey: {
      type: String,
      default: "",
    },
    busySourceKeys: {
      type: Object,
      default: () => ({}),
    },
    autoMatchBusy: {
      type: Boolean,
      default: false,
    },
    autoMatchEnabled: {
      type: Boolean,
      default: false,
    },
    entityCode: {
      type: String,
      default: "",
    },
    initialCachedRecords: {
      type: Array,
      default: () => [],
    },
    initialCachedPartnerRecords: {
      type: Array,
      default: () => [],
    },
    initialCachedConsumWarehouseRecords: {
      type: Array,
      default: () => [],
    },
    seasonOptions: {
      type: Array,
      default: () => [],
    },
    selectedSeasonUuid: {
      type: String,
      default: "",
    },
    requireSeason: {
      type: Boolean,
      default: false,
    },
    mappingStatus: {
      type: String,
      default: "all",
    },
  },
  data() {
    return {
      remoteOperations: { paging: true, filtering: true },
      allowedPageSizes: [10, 25, 50, 100],
      erpSelectDataSource: null,
      partnerSelectDataSource: null,
      consumWarehouseSelectDataSource: null,
      serviceArticleSelectDataSource: null,
      recordsCache: new Map(),
      partnerRecordsCache: new Map(),
      consumWarehouseRecordsCache: new Map(),
    };
  },
  computed: {
    showSeasonSelector() {
      return this.requireSeason && this.entityCode === "PARCELS";
    },
    mappingStatusOptions() {
      return [
        { value: "all", label: this.$t("tools.erp_integration.mapping_status_all") },
        { value: "mapped", label: this.$t("tools.erp_integration.mapping_status_mapped") },
        { value: "unmapped", label: this.$t("tools.erp_integration.mapping_status_unmapped") },
      ];
    },
  },
  watch: {
    entityCode: {
      immediate: true,
      handler(newCode) {
        this.recordsCache.clear();
        this.partnerRecordsCache.clear();
        this.consumWarehouseRecordsCache.clear();
        this.populateCacheWithRecords(this.initialCachedRecords);
        this.populateCacheWithRecords(this.initialCachedPartnerRecords, this.partnerRecordsCache);
        this.populateCacheWithRecords(this.initialCachedConsumWarehouseRecords, this.consumWarehouseRecordsCache);
        this.initErpDataSource(newCode);
      },
    },
    initialCachedRecords: {
      immediate: true,
      deep: true,
      handler(records) {
        this.populateCacheWithRecords(records);
      },
    },
    initialCachedPartnerRecords: {
      immediate: true,
      handler(records) {
        this.populateCacheWithRecords(records, this.partnerRecordsCache);
      },
    },
    initialCachedConsumWarehouseRecords: {
      immediate: true,
      handler(records) {
        this.populateCacheWithRecords(records, this.consumWarehouseRecordsCache);
      },
    },
    isStaticMode: {
      handler() {
        this.initErpDataSource(this.entityCode);
      },
    },
  },
  methods: {
    onSeasonChanged(e) {
      this.$emit("season-changed", e?.value || "");
    },
    onMappingStatusValueChanged(e) {
      const value = e?.value || "all";

      if (value === this.mappingStatus) {
        return;
      }

      this.$emit("mapping-status-changed", value);
    },
    populateCacheWithRecords(records, cache = this.recordsCache) {
      if (!Array.isArray(records)) return;
      for (const record of records) {
        if (record && record.id) {
          const strId = String(record.id);
          cache.set(strId, {
            value: strId,
            label: `${record.external_key || record.id} - ${this.formatRecordDisplayName(record)}`,
            raw: record,
          });
        }
      }
    },
    formatRecordDisplayName(record) {
      const raw = record?.raw_data || {};
      return (
        raw.Denumire ||
        raw.DENUMIRE ||
        raw.denumire ||
        raw.Name ||
        raw.name ||
        raw.NAME ||
        raw.Nume ||
        raw.NUME ||
        raw.nume ||
        raw.DenumirePartener ||
        raw.DENUMIRE_PARTENER ||
        record.external_key ||
        record.id ||
        ""
      );
    },
    initErpDataSource(entityCode) {
      this.erpSelectDataSource = entityCode && !this.isStaticMode
        ? this.createErpDataSource(entityCode, this.recordsCache) : null;
      this.partnerSelectDataSource = entityCode === "LEGAL_ENTITIES"
        ? this.createErpDataSource("PARTNERS", this.partnerRecordsCache) : null;
      this.consumWarehouseSelectDataSource = entityCode === "LEGAL_ENTITIES"
        ? this.createErpDataSource("WAREHOUSES", this.consumWarehouseRecordsCache) : null;
      this.serviceArticleSelectDataSource = entityCode === "PRODUCTS" && !this.isStaticMode
        ? this.createErpDataSource(entityCode, this.recordsCache) : null;
    },
    createErpDataSource(entityCode, cache) {
      const formatDisplayName = this.formatRecordDisplayName;

      const customStore = new CustomStore({
        key: "value",
        loadMode: "processed",
        useDefaultSearch: false,
        byKey: async (key) => {
          if (key === null || typeof key === "undefined" || key === "") {
            return null;
          }
          const strKey = String(key);
          if (cache.has(strKey)) {
            return cache.get(strKey);
          }

          try {
            const url = endpoints.replaceParams(endpoints.ERP_ENTITY_RECORDS, { entityCode });
            const response = await axios.get(url, { params: { ids: strKey, limit: 1 } });
            const rows = response?.data?.data?.data || response?.data?.data || [];
            const record = Array.isArray(rows) ? rows.find((r) => String(r.id) === strKey) : null;
            if (record) {
              const item = {
                value: String(record.id),
                label: `${record.external_key || record.id} - ${formatDisplayName(record)}`,
                raw: record,
              };
              cache.set(strKey, item);
              return item;
            }
          } catch (e) {
            console.error("Error fetching ERP record by key:", e);
            if (["PARTNERS", "PRODUCTS", "WAREHOUSES"].includes(entityCode)) throw e;
          }

          if (["PARTNERS", "PRODUCTS", "WAREHOUSES"].includes(entityCode)) {
            throw new Error(`ERP ${entityCode} record ${strKey} was not found`);
          }
          const fallbackItem = { value: strKey, label: strKey };
          cache.set(strKey, fallbackItem);
          return fallbackItem;
        },
        load: async (loadOptions) => {
          const limit = loadOptions.take || 20;
          const skip = loadOptions.skip || 0;
          const page = Math.floor(skip / limit) + 1;
          const search = loadOptions.searchValue || extractContainsSearch(loadOptions.filter) || "";

          const url = endpoints.replaceParams(endpoints.ERP_ENTITY_RECORDS, { entityCode });
          const params = {
            page,
            limit,
          };
          if (search && String(search).trim() !== "") {
            params.search = String(search).trim();
            params.search_display = 1;
          }

          try {
            const response = await axios.get(url, { params });
            const rawData = response?.data?.data;
            let records = [];
            let totalCount = 0;

            if (Array.isArray(rawData)) {
              records = rawData;
              totalCount = rawData.length;
            } else if (Array.isArray(rawData?.data)) {
              records = rawData.data;
              totalCount = rawData.pagination?.total ?? rawData.meta?.total ?? records.length;
            } else if (entityCode === "WAREHOUSES") {
              throw new Error("Invalid ERP warehouse records response");
            }

            const items = records.map((record) => {
              const item = {
                value: String(record.id),
                label: `${record.external_key || record.id} - ${formatDisplayName(record)}`,
                raw: record,
              };
              cache.set(String(record.id), item);
              return item;
            });

            return {
              data: items,
              totalCount,
            };
          } catch (e) {
            console.error("Error loading ERP records for select box:", e);
            if (["PARTNERS", "PRODUCTS", "WAREHOUSES"].includes(entityCode)) throw e;
            return { data: [], totalCount: 0 };
          }
        },
      });

      // Each SelectBox owns its DataSource; only the store and record cache are shared.
      return {
        store: customStore,
        paginate: true,
        pageSize: 20,
        searchExpr: "label",
        searchOperation: "contains",
      };
    },
    onRefreshMappings() {
      this.$emit("refresh");
    },
    sourceKey(sourceRow) {
      const raw = sourceRow?.[this.keyField];
      return raw === null || typeof raw === "undefined" ? "" : String(raw);
    },
    displaySourceName(sourceRow) {
      return (
        sourceRow?.name ||
        sourceRow?.department_name ||
        sourceRow?.Denumire ||
        sourceRow?.Nume ||
        sourceRow?.Name ||
        sourceRow?.code ||
        sourceRow?.uuid ||
        "-"
      );
    },
    getDepartmentName(sourceRow) {
      return (
        sourceRow?.department?.name ||
        sourceRow?.department_name ||
        sourceRow?.department ||
        "-"
      );
    },
    selectedValue(sourceRow) {
      const mapping = this.mappingBySourceKey[this.sourceKey(sourceRow)];
      if (this.isStaticMode) {
        return mapping?.staticValue || "";
      }
      return mapping?.erpRecordId || "";
    },
    partnerValue(sourceRow) {
      return this.mappingBySourceKey[this.sourceKey(sourceRow)]?.erpPartnerRecordId || "";
    },
    consumWarehouseValue(sourceRow) {
      return this.mappingBySourceKey[this.sourceKey(sourceRow)]?.erpConsumWarehouseRecordId || "";
    },
    serviceArticleValue(sourceRow) {
      return this.mappingBySourceKey[this.sourceKey(sourceRow)]?.serviceStaticValue || "";
    },
    isMappingBusy(sourceRow) {
      const key = this.sourceKey(sourceRow);
      return !!this.loading || this.busySourceKey === key || !!this.busySourceKeys?.[key];
    },
    onServiceArticleSelectionChanged(sourceRow, value) {
      const normalizedValue = value === null || typeof value === "undefined" ? "" : String(value).trim();
      if (normalizedValue === this.serviceArticleValue(sourceRow) || this.isMappingBusy(sourceRow)) {
        return;
      }
      this.$emit("save-service-mapping", { sourceRow, value: normalizedValue || null });
    },
    onSelectionChanged(sourceRow, value) {
      if (String(value || "").trim() === this.selectedValue(sourceRow) || this.isMappingBusy(sourceRow)) {
        return;
      }
      this.$emit("save-mapping", { sourceRow, value: value || null });
    },
    onPartnerSelectionChanged(sourceRow, value) {
      if (String(value || "").trim() === this.partnerValue(sourceRow) || this.isMappingBusy(sourceRow)) {
        return;
      }
      this.$emit("save-partner-mapping", { sourceRow, value: value || null });
    },
    onConsumWarehouseSelectionChanged(sourceRow, value) {
      if (String(value || "").trim() === this.consumWarehouseValue(sourceRow) || this.isMappingBusy(sourceRow)) {
        return;
      }
      this.$emit("save-consum-warehouse-mapping", { sourceRow, value: value || null });
    },
    clearMapping(sourceRow) {
      if (this.isMappingBusy(sourceRow)) {
        return;
      }
      this.$emit("clear-mapping", { sourceRow });
    },
    repaintMappingRow(sourceKey) {
      const grid = this.$refs.mappingsGrid?.instance;
      const rowIndex = grid?.getRowIndexByKey(this.keyField === "id" ? Number(sourceKey) : sourceKey);
      if (rowIndex >= 0) {
        grid.repaintRows([rowIndex]);
      }
    },
    reload() {
      return this.$refs.mappingsGrid?.instance?.refresh();
    },
  },
};
</script>

<style scoped>
.erp-mappings-grid {
  height: auto;
}

.mapping-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.mapping-delete-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  flex-shrink: 0;
}

.mapping-refresh-button {
  width: 32px;
  height: 32px;
}

.erp-mappings-grid .erp-mapping-input {
  height: 32px;
  margin: 0;
  padding: 4px 8px;
  font-size: 12px;
}

.erp-mappings-grid ::v-deep .erp-mapping-select {
  height: 32px;
}

.erp-mappings-grid ::v-deep .erp-mapping-select .dx-texteditor-input {
  min-height: 30px;
  padding: 4px 8px;
  font-size: 12px;
}

.card-block {
  border: 1px solid #dbe4ef;
  border-radius: 10px;
  background: #fff;
  padding: 14px;
  margin-top: 14px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1b3559;
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px 24px;
}

.season-filter {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  margin-bottom: 12px;
  max-width: 420px;
}

.season-filter__label {
  display: block;
  margin-bottom: 0;
  font-weight: 600;
  color: #30465f;
  min-width: 120px;
}

.season-filter__select {
  flex: 1;
  min-width: 220px;
}

.season-filter-empty {
  border: 1px dashed #c7d3e4;
  border-radius: 8px;
  padding: 14px;
  background: #f8fbff;
  color: #5f6b7a;
}

.erp-mapping-select {
  width: 100%;
}
</style>
